import React, { useCallback, useEffect, useState } from "react";
import { format, subDays } from "date-fns";

import {
  Button,
  makeStyles,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ResponsiveTable from "../../components/ResponsiveTable";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  note: {
    padding: theme.spacing(1, 1.5),
    color: theme.palette.text.secondary,
  },
  secondary: {
    color: theme.palette.text.secondary,
  },
}));

const toInputDate = (date) => format(date, "yyyy-MM-dd");

// 75 -> "1 min 15 s", 5400 -> "1 h 30 min"
const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const rest = seconds % 60;
    return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes ? `${hours} h ${restMinutes} min` : `${hours} h`;
};

const formatPercent = (part, total) =>
  total ? `${Math.round((part / total) * 100)}%` : "—";

const Reports = () => {
  const classes = useStyles();

  const [from, setFrom] = useState(toInputDate(subDays(new Date(), 6)));
  const [to, setTo] = useState(toInputDate(new Date()));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/response-times", {
        params: { from, to },
      });
      setReport(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [from, to]);

  useEffect(() => {
    void fetchReport();
    // Load once on open; later loads use the button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fastMinutes = report
    ? Math.round(report.fastResponseSeconds / 60)
    : 5;
  const columnLabel = (column) =>
    i18n.t(`reports.table.${column}`, { fast: fastMinutes });

  const columns = [
    "user",
    "tickets",
    "firstResponse",
    "response",
    "fast",
    "unanswered",
  ];

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("reports.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            label={i18n.t("reports.from")}
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={i18n.t("reports.to")}
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={fetchReport}
            disabled={loading || !from || !to}
          >
            {i18n.t("reports.buttons.update")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {report && (
          <Typography variant="body2" className={classes.note}>
            {i18n.t("reports.note", {
              start: report.businessHours.start,
              end: report.businessHours.end,
              fast: fastMinutes,
            })}
          </Typography>
        )}
        <ResponsiveTable>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column}
                  align={column === "user" ? "left" : "center"}
                >
                  {columnLabel(column)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {!loading &&
                report &&
                report.rows.map((row) => (
                  <TableRow key={row.userId ?? "unassigned"}>
                    <TableCell>
                      {row.userName ?? i18n.t("reports.unassigned")}
                    </TableCell>
                    <TableCell
                      align="center"
                      data-label={columnLabel("tickets")}
                    >
                      {row.tickets}
                    </TableCell>
                    <TableCell
                      align="center"
                      data-label={columnLabel("firstResponse")}
                    >
                      {formatDuration(row.firstResponseAvg)}
                      <div className={classes.secondary}>
                        {i18n.t("reports.median")}{" "}
                        {formatDuration(row.firstResponseMedian)}
                      </div>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-label={columnLabel("response")}
                    >
                      {formatDuration(row.responseAvg)}
                      <div className={classes.secondary}>
                        {i18n.t("reports.median")}{" "}
                        {formatDuration(row.responseMedian)}
                      </div>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-label={columnLabel("fast")}
                    >
                      {formatPercent(row.fastAnswered, row.answered)}
                    </TableCell>
                    <TableCell
                      align="center"
                      data-label={columnLabel("unanswered")}
                    >
                      {row.unanswered}
                    </TableCell>
                  </TableRow>
                ))}
              {loading && <TableRowSkeleton columns={columns.length} />}
            </>
          </TableBody>
        </ResponsiveTable>
      </Paper>
    </MainContainer>
  );
};

export default Reports;
